import { useEffect, useRef } from 'react';
import { eegAt, dsaColumn } from '../../engine/eegEngine';

/** DPR-aware canvas sizing shared by the three canvases below. */
function useSizedCanvas(canvasRef, dimsRef) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      dimsRef.current = { width: rect.width, height: rect.height };
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };
    resize();
    const obs = new ResizeObserver(resize);
    obs.observe(canvas);
    return () => obs.disconnect();
  }, [canvasRef, dimsRef]);
}

/**
 * Scrolling raw-EEG strip (newest sample at the right edge, like a BIS/SedLine
 * waveform window). `windowSec` of trace is visible; `channel` picks the lead.
 */
export function EegTrace({ indexRef, channel = 0, color = '#E8F4FF', windowSec = 8, gain = 1, grid = true }) {
  const canvasRef = useRef(null);
  const dimsRef = useRef({ width: 0, height: 0 });
  useSizedCanvas(canvasRef, dimsRef);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    let raf;
    const t0 = performance.now() / 1000;
    const draw = () => {
      const { width: W, height: H } = dimsRef.current;
      if (W && H) {
        const now = performance.now() / 1000 - t0;
        const idx = indexRef.current;
        ctx.clearRect(0, 0, W, H);
        if (grid) {
          ctx.strokeStyle = 'rgba(255,255,255,0.07)';
          ctx.lineWidth = 1;
          for (let s = 0; s <= windowSec; s++) {
            const x = (s / windowSec) * W;
            ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
          }
          ctx.beginPath(); ctx.moveTo(0, H / 2); ctx.lineTo(W, H / 2); ctx.stroke();
        }
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        const px = Math.max(1, Math.floor(W));
        for (let i = 0; i <= px; i++) {
          const t = now - windowSec + (i / px) * windowSec;
          const v = eegAt(t, idx, channel) * gain;          // µV
          const y = H / 2 - (v / 100) * (H / 2) * 0.92;
          if (i === 0) ctx.moveTo(i, y); else ctx.lineTo(i, y);
        }
        ctx.stroke();
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [indexRef, channel, color, windowSec, gain, grid]);

  return <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />;
}

/**
 * Density spectral array: time → right, 0–30 Hz → up, colour = power.
 * One column per `stepMs`; keeps the last `columns` columns.
 */
export function DsaCanvas({ indexRef, columns = 240, stepMs = 1000, sefRef = null, accent = '#FF8A3D' }) {
  const canvasRef = useRef(null);
  const dimsRef = useRef({ width: 0, height: 0 });
  const colsRef = useRef([]);
  useSizedCanvas(canvasRef, dimsRef);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    let raf, last = 0;
    const paint = () => {
      const { width: W, height: H } = dimsRef.current;
      if (!W || !H) return;
      const cols = colsRef.current;
      ctx.fillStyle = '#05060a';
      ctx.fillRect(0, 0, W, H);
      const cw = W / columns;
      const bins = 30;
      const bh = H / bins;
      cols.forEach((col, ci) => {
        const x = W - (cols.length - ci) * cw;
        for (let b = 0; b < bins; b++) {
          const p = col.pw[b];
          // Masimo-style palette: blue → green → yellow → red.
          const hue = 240 - p * 240;
          ctx.fillStyle = `hsl(${hue}, 90%, ${25 + p * 35}%)`;
          ctx.fillRect(x, H - (b + 1) * bh, Math.ceil(cw), Math.ceil(bh));
        }
      });
      // SEF95 line over the DSA.
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1;
      ctx.beginPath();
      cols.forEach((col, ci) => {
        const x = W - (cols.length - ci) * cw + cw / 2;
        const y = H - (col.sef / 30) * H;
        if (ci === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      ctx.stroke();
      // Frequency ticks.
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      ctx.font = '9px Barlow, sans-serif';
      [5, 10, 15, 20, 25].forEach((f) => ctx.fillText(`${f}`, 2, H - (f / 30) * H + 3));
    };
    const tick = (ts) => {
      if (ts - last >= stepMs) {
        last = ts;
        const idx = indexRef.current;
        const cols = colsRef.current;
        cols.push({ pw: dsaColumn(idx), sef: sefRef?.current ?? 15 });
        if (cols.length > columns) cols.shift();
        paint();
      }
      raf = requestAnimationFrame(tick);
    };
    // Pre-fill so the display is not blank for minutes after opening.
    if (colsRef.current.length === 0) {
      for (let i = 0; i < columns; i++) colsRef.current.push({ pw: dsaColumn(indexRef.current), sef: sefRef?.current ?? 15 });
    }
    paint();
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [indexRef, columns, stepMs, sefRef, accent]);

  return <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />;
}

/**
 * Index trend: last `minutes` of the depth index, with the target band shaded.
 * Samples the index every `stepMs`.
 */
export function TrendCanvas({ indexRef, minutes = 30, stepMs = 2000, band = [40, 60], color = '#4FC3F7', srRef = null }) {
  const canvasRef = useRef(null);
  const dimsRef = useRef({ width: 0, height: 0 });
  const ptsRef = useRef([]);
  useSizedCanvas(canvasRef, dimsRef);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    let raf, last = 0;
    const maxPts = Math.round((minutes * 60000) / stepMs);
    const paint = () => {
      const { width: W, height: H } = dimsRef.current;
      if (!W || !H) return;
      ctx.clearRect(0, 0, W, H);
      // Target band.
      ctx.fillStyle = 'rgba(255,255,255,0.06)';
      ctx.fillRect(0, H - (band[1] / 100) * H, W, ((band[1] - band[0]) / 100) * H);
      // Gridlines every 20.
      ctx.strokeStyle = 'rgba(255,255,255,0.08)';
      ctx.lineWidth = 1;
      ctx.fillStyle = 'rgba(255,255,255,0.45)';
      ctx.font = '9px Barlow, sans-serif';
      for (let v = 0; v <= 100; v += 20) {
        const y = H - (v / 100) * H;
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
        if (v > 0 && v < 100) ctx.fillText(String(v), 2, y - 2);
      }
      const pts = ptsRef.current;
      if (pts.length < 2) return;
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      pts.forEach((p, i) => {
        const x = W - ((pts.length - 1 - i) / maxPts) * W;
        const y = H - (p.v / 100) * H;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      ctx.stroke();
      if (srRef) {
        ctx.strokeStyle = 'rgba(255,80,80,0.9)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        pts.forEach((p, i) => {
          const x = W - ((pts.length - 1 - i) / maxPts) * W;
          const y = H - (p.sr / 100) * H;
          if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        });
        ctx.stroke();
      }
    };
    const tick = (ts) => {
      if (ts - last >= stepMs) {
        last = ts;
        const pts = ptsRef.current;
        pts.push({ v: indexRef.current, sr: srRef?.current ?? 0 });
        if (pts.length > maxPts) pts.shift();
        paint();
      }
      raf = requestAnimationFrame(tick);
    };
    paint();
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [indexRef, minutes, stepMs, band, color, srRef]);

  return <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />;
}
